// Checks on a patient message the practice writes itself (Admin > Settings).
//
// The app fills {tags} in; anything it does not recognise is sent exactly as
// typed, and a message with no {link} gives the patient nothing to tap. Both
// are easy to do and invisible until a patient replies "what link?", so the
// editor says so while the message is still being written.

const TAG = /\{(\w+)\}/g;

/** Plain-language warnings about a message. Empty means the standard wording, which is always fine. */
export function templateProblems(
  text: string,
  known: readonly string[],
  // A message sent after the patient has booked carries no link to tap, and
  // is not warned for having none.
  opts: { requireLink?: boolean } = {},
): string[] {
  const message = String(text || '');
  if (!message.trim()) return [];

  const problems: string[] = [];
  const used = Array.from(message.matchAll(TAG), (m) => m[1]);

  if (opts.requireLink !== false && !used.includes('link')) {
    problems.push('There is no {link} in this message, so the patient would have nothing to tap.');
  }

  Array.from(new Set(used)).filter((name) => !known.includes(name)).forEach((name) => {
    const meant = known.find((k) => k.toLowerCase() === name.toLowerCase());
    problems.push(meant
      ? `{${name}} is not a tag the app fills in. Did you mean {${meant}}?`
      : `{${name}} is not a tag the app fills in. The patient would see it exactly as typed.`);
  });

  return problems;
}

/** Put a tag where the cursor is (or over the selection); with no cursor, at the end. */
export function insertTag(
  text: string,
  start: number | null | undefined,
  end: number | null | undefined,
  name: string,
): { text: string; caret: number } {
  const value = String(text || '');
  const from = typeof start === 'number' ? start : value.length;
  const to = typeof end === 'number' ? end : from;
  const tag = `{${name}}`;
  return { text: value.slice(0, from) + tag + value.slice(to), caret: from + tag.length };
}
